import { DataTable, useColumns } from "@/components/data-table";
import { DataFetcher } from "@/components/display";
import { useFilters } from "@/components/filters";
import { SchemaFormHook } from "@/components/form";
import { ListPage } from "@/components/list-page";
import { ConfirmDialog, Dialog, Drawer, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import { getAllRubros } from "@/features/auxiliar/rubros/api/get-all-rubros";
import { getRubro } from "@/features/auxiliar/rubros/api/get-rubro";
import CsvExportDialog from "@/features/export/components/csv-export-dialog";
import PrintExportDialog from "@/features/export/components/print-dialog-obs";
import XlsxExportDialog from "@/features/export/components/xlsx-export-dialog";
import { AddArticuloMutation } from "@/features/inventario/articulos/api/add-articulo";
import { useGetNextCodigoQuery } from "@/features/inventario/articulos/api/get-next-codigo";
import { DeleteArticuloMutation } from "@/features/inventario/articulos/api/delete-articulo";
import { EditArticuloMutation } from "@/features/inventario/articulos/api/edit-articulo";
import { EditArticuloCantidadMutation } from "@/features/inventario/articulos/api/edit-articulo-cantidad";
import { getAllArticulos, useGetAllArticulosQuery } from "@/features/inventario/articulos/api/get-all-articulos";
import { GetArticuloQuery } from "@/features/inventario/articulos/api/get-articulo";
import ArticuloForm, { articuloFormSchema } from "@/features/inventario/articulos/components/articulo-form";
import EditArticuloCantidadForm, { editArticuloCantidadFormSchema } from "@/features/inventario/articulos/components/edit-articulo-cantidad-form";
import ViewArticulo from "@/features/inventario/articulos/components/view-articulo";
import ViewArticuloHistorial from "@/features/inventario/articulos/components/view-articulo-historial";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { formatDate, formatNumber, formatString } from "@/shared/formats";
import { notify, notifyError, SilentSubmitError } from "@/shared/notification";
import { Permiso } from "@/shared/types/permiso";
import InventoryIcon from '@mui/icons-material/Inventory';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';

export default function ArticulosPage() {
    const openModal = useOpenModal();

    const { monedaBase } = useMonetizacion();

    const hasWritePermiso = useHasPermisos(Permiso.WriteArticulos);
    const nextCodigoQuery = useGetNextCodigoQuery();
    const hasReadHistorialPermiso = useHasPermisos(Permiso.ReadArticuloHistorial);
    const hasWriteCantidadPermiso = useHasPermisos(Permiso.WriteArticulosCantidad);

    const filterData = useFilters({
        filters: [
            { type: "string", name: "nombre", label: "Nombre" },
            { type: "string", name: "codigo", label: "Código" },
            { type: "string", name: "numeroItem", label: "N° de Item" },
            { type: "model", name: "rubroId", label: "Rubro", get: getRubro, getAll: getAllRubros },
            { type: "pagination" }
        ],
    })

    const fetchData = useGetAllArticulosQuery(filterData.filterData);

    const columnData = useColumns({
        key: "articulos",
        columns: [
            { key: "codigo", header: "Código", render: a => formatString(a.codigo), width: "auto" },
            { key: "numeroItem", header: "Número", render: a => formatString(a.numeroItem), width: "auto" },
            { key: "nombre", header: "Nombre", render: a => formatString(a.nombre) },
            { key: "rubro", header: "Rubro", render: a => formatString(a.rubroNombre) },
            { key: "cantidad", header: "Cantidad", render: a => formatNumber(a.cantidad), align: "right", width: "auto", show: true },
            { key: "costo", header: `Costo (${monedaBase.simbolo})`, render: a => formatNumber(a.costo), align: "right", width: "auto", show: false },
            { key: "precio1", header: `Precio 1 (${monedaBase.simbolo})`, render: a => formatNumber(a.precios[0]), align: "right", width: "auto", show: true },
            { key: "precio2", header: `Precio 2 (${monedaBase.simbolo})`, render: a => formatNumber(a.precios[1]), align: "right", width: "auto", show: false },
            { key: "precio3", header: `Precio 3 (${monedaBase.simbolo})`, render: a => formatNumber(a.precios[2]), align: "right", width: "auto", show: false },
            { key: "iva", header: "IVA (%)", render: a => a.iva === 0 ? "Exento" : a.iva, align: "right", width: "auto", show: false },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "creacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: true },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "modificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false }
        ]
    });

    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Artículos">
                <ListPage.Header.Actions.ColumnButton columnData={columnData} />
                <ListPage.Header.Actions.FilterButton />
                {hasWritePermiso && <ListPage.Header.Actions.AddButton onClick={() => openModal("add")} />}
                <ListPage.Header.Actions.Divider />
                <ListPage.Header.Actions.ExportButton onClick={() => openModal("print")}>
                    <ListPage.Header.Actions.ExportButton.Print onClick={() => openModal("print")} />
                    <ListPage.Header.Actions.ExportButton.Csv onClick={() => openModal("csv")} />
                    <ListPage.Header.Actions.ExportButton.Xlsx onClick={() => openModal("xlsx")} />
                </ListPage.Header.Actions.ExportButton>
            </ListPage.Header.Actions>
            <ListPage.Header.Filters filterData={filterData} />
        </ListPage.Header>
        <DataTable flex={1}>
            <DataTable.Content
                rows={fetchData.data?.result}
                columns={[
                    ...columnData.columns,
                    {
                        header: "Acción", width: "auto", align: "center", render: row => <DataTable.EntryOption>
                            <DataTable.EntryOption.ViewButton onClick={() => openModal("view", row.id)} />
                            {hasReadHistorialPermiso && <DataTable.EntryOption.Button Icon={ManageSearchIcon} title="Historial" onClick={() => openModal("history", row.id)} />}
                            {hasWritePermiso && <DataTable.EntryOption.EditButton onClick={() => openModal("edit", row.id)} />}
                            {hasWriteCantidadPermiso && <DataTable.EntryOption.Button Icon={InventoryIcon} title="Editar Cantidad" onClick={() => openModal("editCantidad", row.id)} />}
                            {hasWritePermiso && <DataTable.EntryOption.DeleteButton disabled={row.protegido} onClick={() => openModal("delete", row)} />}
                        </DataTable.EntryOption>
                    }
                ]}
                fit="flex"
                loading={fetchData.isFetching}
                error={fetchData.error}
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>

        <Dialog code="view" size="md">
            <ModalHeader>Ver Artículo</ModalHeader>
            <ModalValueExtractor>
                {articuloId => <GetArticuloQuery id={articuloId} includeImage>
                    {query => <DataFetcher query={query}>
                        {articulo => <ViewArticulo articulo={articulo} />}
                    </DataFetcher>}
                </GetArticuloQuery>}
            </ModalValueExtractor>
        </Dialog>

        {hasReadHistorialPermiso && <>
            <Dialog code="history" size="lg">
                <ModalHeader>Historial</ModalHeader>
                <ModalValueExtractor>
                    {articuloId => <ViewArticuloHistorial articuloId={articuloId} padding={3} />}
                </ModalValueExtractor>
            </Dialog>
        </>}

        {hasWriteCantidadPermiso && <>
            <Drawer code="editCantidad">
                <ModalHeader>Editar Cantidad</ModalHeader>
                <ModalValueExtractor>
                    {articuloId => <GetArticuloQuery id={articuloId}>
                        {query => <DataFetcher flex={1} query={query}>
                            {articulo => <EditArticuloCantidadMutation id={articulo.id}>
                                {mutation => <SchemaFormHook
                                    schema={editArticuloCantidadFormSchema}
                                    onSubmit={async ({ value }) => {
                                        await mutation.mutateAsync(value);
                                        notify("La cantidad del artículo se ha modificado correctamente.");
                                    }}
                                    defaultValues={{ cantidad: articulo.cantidad }}
                                    submitOnce
                                    closeModalOnSubmit
                                >
                                    {form => <EditArticuloCantidadForm form={form} padding={3} />}
                                </SchemaFormHook>}
                            </EditArticuloCantidadMutation>}
                        </DataFetcher>}
                    </GetArticuloQuery>}
                </ModalValueExtractor>
            </Drawer>
        </>}

        {hasWritePermiso && <>
            <Dialog code="add" size="md" >
                <ModalHeader>Agregar Articulo</ModalHeader>
                <AddArticuloMutation>
                    {mutation => <SchemaFormHook
                        schema={articuloFormSchema}
                        onSubmit={async ({ value }) => {
                            try {
                                await mutation.mutateAsync(value);
                                notify("El artículo se ha agregado correctamente.");
                            } catch (error) {
                                notifyError(error);
                                throw new SilentSubmitError();
                            }
                        }}
                        defaultValues={{ codigo: nextCodigoQuery.data?.toString() ?? "" }}
                        submitOnce
                        closeModalOnSubmit
                    >
                        {form => <ArticuloForm form={form} padding={3} />}
                    </SchemaFormHook>}
                </AddArticuloMutation>
            </Dialog>

            <Dialog code="edit" size="md" >
                <ModalHeader>Editar Articulo</ModalHeader>
                <ModalValueExtractor>
                    {articuloId => <GetArticuloQuery id={articuloId} includeImage>
                        {query => <DataFetcher flex={1} query={query}>
                            {articulo => <EditArticuloMutation id={articulo.id}>
                                {mutation => <SchemaFormHook
                                    schema={articuloFormSchema}
                                    onSubmit={async ({ value }) => {
                                        await mutation.mutateAsync(value);
                                        notify("El artículo se ha editado correctamente.");
                                    }}
                                    onSubmitInvalid={({ formApi }) => {
                                        console.log(formApi.state.errors)
                                    }}
                                    defaultValues={{
                                        cantidadMinima: articulo.cantidadMinima || "",
                                        cantidadPorCaja: articulo.cantidadPorCaja || "",
                                        codigo: articulo.codigo,
                                        costo: articulo.costo,
                                        descripcion: articulo.descripcion || "",
                                        imagen: articulo.imagen || null,
                                        iva: articulo.iva,
                                        nombre: articulo.nombre,
                                        numeroItem: articulo.numeroItem || "",
                                        precios: articulo.precios,
                                        rubroId: articulo.rubroId
                                    }}
                                    submitOnce
                                    closeModalOnSubmit
                                >
                                    {form => <ArticuloForm form={form} padding={3} />}
                                </SchemaFormHook>}
                            </EditArticuloMutation>}
                        </DataFetcher>}
                    </GetArticuloQuery>}
                </ModalValueExtractor>
            </Dialog>

            <ConfirmDialog code="delete">
                <ModalValueExtractor>
                    {articulo => <DeleteArticuloMutation id={articulo.id}>
                        {mutation => <ConfirmDialog.Content
                            title={`Borrar Artículo "${articulo.nombre}"`}
                            description={`¿Estás seguro que quieres borrar el artículo "${articulo.nombre}"?`}
                            onConfirm={async () => {
                                await mutation.mutateAsync({});
                                notify(`El artículo "${articulo.nombre}" se ha borrado correctamente.`, { variant: "success" });
                            }}
                        />}
                    </DeleteArticuloMutation>}
                </ModalValueExtractor>
            </ConfirmDialog>
        </>}

        <PrintExportDialog
            code="print"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllArticulos}
            defaultFileTitle="Artículos"
        />

        <CsvExportDialog
            code="csv"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllArticulos}
            defaultFileTitle="Artículos"
        />

        <XlsxExportDialog
            code="xlsx"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllArticulos}
            defaultFileTitle="Artículos"
        />
    </ListPage>
}